import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useReducer, useState, forwardRef, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { AttachFileIcon } from '@skatteetaten/ds-icons';
import { Spinner } from '@skatteetaten/ds-progress';
import { Alert } from '@skatteetaten/ds-status';
import { List } from '@skatteetaten/ds-typography';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { FileUploaderFile } from '../FileUploaderFile/index.esm.js';
import { LabelWithHelp } from '../LabelWithHelp/index.esm.js';
import './styles.css';

import '@skatteetaten/ds-buttons';
import '../Help/index.esm.js';
import './styles.css';


var FileActionType = /*#__PURE__*/ function(FileActionType) {
    FileActionType["SUCCESS"] = "SUCCESS";
    FileActionType["ERROR"] = "ERROR";
    FileActionType["REMOVE"] = "REMOVE";
    return FileActionType;
}({});

const useFileUploader = (renderStatus, initiallyUploadedFiles)=>{
    const reducer = (state, action)=>{
        switch(action.type){
            case 'ERROR':
                {
                    return action.successFiles ? state.concat(action.successFiles) : state;
                }
            case 'SUCCESS':
                {
                    return state.concat(action.files);
                }
            case 'REMOVE':
                {
                    if (action.file.id) {
                        return state.filter((file)=>file.id !== action.file.id);
                    } else {
                        return state.filter((file)=>file.name !== action.file.name);
                    }
                }
        }
        return [
            ...state
        ];
    };
    const initialState = initiallyUploadedFiles ?? [];
    const [state, dispatch] = useReducer(reducer, initialState);
    const [uploadResult, setUploadResult] = useState();
    const [isUploading, setIsUploading] = useState(false);
    const setSuccess = (files, data)=>{
        setIsUploading(false);
        files.forEach((file)=>{
            if (file.id && state.some((it)=>it.id === file.id)) {
                throw Error('Filen er allerede i lista.');
            }
        });
        dispatch({
            type: FileActionType.SUCCESS,
            files
        });
        const successStatus = dsI18n.t('ds_forms:fileuploader.UploadedSummary', {
            fileCount: files.length
        });
        setUploadResult({
            uploadedFilesMessage: successStatus,
            data
        });
    };
    const setLoading = ()=>{
        setUploadResult(undefined);
        setIsUploading(true);
    };
    const setFailure = (files, errorMessage, succeeded)=>{
        setIsUploading(false);
        dispatch({
            type: FileActionType.ERROR,
            files,
            successFiles: succeeded
        });
        files.forEach((file)=>{
            if (file.id && state.some((it)=>it.id === file.id)) {
                throw new Error('Filen er allerede i lista.');
            }
        });
        setUploadResult({
            data: errorMessage,
            hasUploadFailed: true
        });
    };
    const remove = (file)=>{
        dispatch({
            type: FileActionType.REMOVE,
            file
        });
        setUploadResult(undefined);
    };
    const hasName = (value)=>typeof value === 'object' && value !== null && 'name' in value && typeof value.name === 'string';
    const isUploadError = (uploadStatusData)=>{
        return Array.isArray(uploadStatusData) && !uploadStatusData.some((data)=>{
            if (Array.isArray(data.files)) {
                if (data.files.some((file)=>!hasName(file))) {
                    return true;
                }
                return typeof data.error !== 'string';
            }
            return true;
        });
    };
    const renderFileStatusMessage = (status)=>{
        if (renderStatus) {
            return renderStatus(status);
        }
        if (status.hasUploadFailed && isUploadError(status.data)) {
            return status.data?.map((error)=>/*#__PURE__*/ jsxs(React.Fragment, {
                    children: [
                        /*#__PURE__*/ jsx("span", {
                            children: `${error.error}:`
                        }),
                        /*#__PURE__*/ jsx(List, {
                            children: error.files.map((file)=>/*#__PURE__*/ jsx(List.Element, {
                                    children: file.name
                                }, file.id ?? file.name))
                        })
                    ]
                }, error.error));
        } else {
            return status.uploadedFilesMessage;
        }
    };
    return [
        {
            uploadedFiles: state,
            uploadResult: uploadResult ? {
                hasUploadFailed: uploadResult?.hasUploadFailed,
                statusMessage: renderFileStatusMessage(uploadResult)
            } : undefined,
            isUploading
        },
        setSuccess,
        setLoading,
        setFailure,
        remove
    ];
};

const dragEvents = [
    'dragover',
    'dragenter',
    'drop'
];
function isDragEvent(event) {
    return dragEvents.includes(event.type);
}
function isChangeEvent(event) {
    return Object.prototype.hasOwnProperty.call(event, 'target');
}
const getFiles = (event)=>{
    if (isDragEvent(event) && event.dataTransfer) {
        const dt = event.dataTransfer;
        if (dt.files && dt.files.length) {
            return Array.from(dt.files);
        }
    } else if (isChangeEvent(event) && event.target.files) {
        return Array.from(event.target.files);
    }
    return [];
};
const nonWordCharacterRegexp = /\W/g;
const fileNameRegex = /\.(?=[^.]+$)/;
const normalize = (file, invalidCharacterRegexp)=>{
    const nameList = file.name.split(fileNameRegex);
    const fileName = nameList[0];
    const normalizedName = fileName.replace(invalidCharacterRegexp || nonWordCharacterRegexp, '_');
    return normalizedName.concat('.', nameList[1]);
};

var styles = {
    "container": "FileUploader-module_container__WJf-V",
    "icon": "FileUploader-module_icon__Pe2KH",
    "dropZone": "FileUploader-module_dropZone__7pUI7",
    "dropZone_error": "FileUploader-module_dropZone_error__nI6nI",
    "dropZone_dragging": "FileUploader-module_dropZone_dragging__BxT45",
    "fileInfo": "FileUploader-module_fileInfo__WwY6w",
    "srOnly": "FileUploader-module_srOnly__ATViY",
    "fileFormatList": "FileUploader-module_fileFormatList__VShdE",
    "fileList": "FileUploader-module_fileList__NTIZp",
    "removePointerEvents": "FileUploader-module_removePointerEvents__x65zk",
    "alert": "FileUploader-module_alert__s6P-Q",
    "spinner": "FileUploader-module_spinner__qqEbU",
    "innerLabel": "FileUploader-module_innerLabel__psX3l"
};

const FileUploader = /*#__PURE__*/ forwardRef(({ id: externalId, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, acceptedFileFormats, acceptedFileFormatsDescription, acceptedFileFormatsDisplay, description, errorMessage, fileIconTitle, helpSvgPath, helpText, label, successIconTitle, titleHelpSvg, uploadResult, uploadedFiles, invalidCharacterRegexp, hideLabel, showRequiredMark, shouldNormalizeFileName, multiple, isUploading, onFileChange, onFileDelete, onFileDownload, onHelpToggle, children: buttonTextExternal }, ref)=>{
    const { t } = useTranslation('ds_forms', {
        i18n: dsI18n
    });
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const afterDeleteFocusRef = useRef(null);
    const [srOnlyText, setSrOnlyText] = useState();
    const [isDragging, setIsDragging] = useState(false);
    const generatedId = useId();
    const [filesPendingDelete, setFilesPendingDelete] = useState({});
    const id = externalId ?? generatedId;
    const errorId = `${useId()}-fileuploader-error`;
    const fileformatsId = `${id}-accepted-formats`;
    const acceptedFormatsAsCommaSeparatedString = acceptedFileFormats?.join(', ');
    const defaultButtonText = multiple ? t('fileuploader.AddMultipleLabel') : t('fileuploader.AddSingleLabel');
    const buttonText = buttonTextExternal ?? defaultButtonText;
    const handleFileChange = (event)=>{
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (isUploading) {
            return;
        }
        const files = getFiles(event);
        if (shouldNormalizeFileName) {
            const normalizedFiles = files.map((file)=>{
                const normalizedName = normalize(file, invalidCharacterRegexp);
                return new File([
                    file
                ], normalizedName, {
                    type: file.type
                });
            });
            onFileChange?.(normalizedFiles);
        } else {
            onFileChange?.(files);
        }
        if (isChangeEvent(event)) {
            /**
         * Resetter verdien slik at Chrome tillatter å laste opp samme fil flere ganger.
         * Det skal være mulig å laste opp slettede filer på nytt.
         */ event.target.value = '';
        }
    };
    const handleDragEnter = (event)=>{
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = (event)=>{
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };
    const handleDragOver = (event)=>{
        event.preventDefault();
        event.stopPropagation();
    };
    const handleDeleteFile = async (file)=>{
        if (!onFileDelete) {
            return;
        }
        const key = file.id ?? file.name;
        /**
       * Her er det viktig at en funksjon sendes inn til setState for å få tak i prevState.
       * Hvis dette ikke gjøres oppstår en race-condition dersom man sletter to filer samtidig.
       */ const timeoutId = setTimeout(()=>{
            setFilesPendingDelete((prevState)=>({
                    ...prevState,
                    [key]: true
                }));
        }, 1000);
        const deletedPromise = onFileDelete(file);
        const deleted = await deletedPromise;
        // hvis sletting er ferdig innen 1 sek skal vi ikke vise spinner
        clearTimeout(timeoutId);
        if (deleted) {
            afterDeleteFocusRef.current?.focus();
            setSrOnlyText(t('fileuploader.DeleteConfirmation'));
        } else {
            setSrOnlyText(t('fileuploader.GeneralDeleteError'));
        }
        setTimeout(()=>{
            setSrOnlyText('');
        }, 3000);
        setFilesPendingDelete((prevState)=>({
                ...prevState,
                [key]: false
            }));
    };
    const concatenatedClassnames = `${styles.container} ${className} ${classNames?.container ?? ''}`.trim();
    const ariaDescribedBy = `${errorMessage ? errorId : ''} ${acceptedFileFormats ? fileformatsId : ''}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        ref: ref,
        className: concatenatedClassnames,
        lang: lang,
        "data-testid": dataTestId,
        children: [
            label && /*#__PURE__*/ jsx(LabelWithHelp, {
                classNames: classNames,
                htmlFor: id,
                hideLabel: hideLabel,
                showRequiredMark: showRequiredMark,
                description: description,
                helpSvgPath: helpSvgPath,
                helpText: helpText,
                titleHelpSvg: titleHelpSvg,
                onHelpToggle: onHelpToggle,
                children: label
            }),
            /*#__PURE__*/ jsx("div", {
                ref: afterDeleteFocusRef,
                tabIndex: -1
            }),
            /*#__PURE__*/ jsx("button", {
                ref: buttonRef,
                type: 'button',
                id: id,
                className: `${styles.dropZone} ${errorMessage ? styles.dropZone_error : ''} ${isDragging && !isUploading ? styles.dropZone_dragging : ''}`.trim(),
                disabled: isUploading,
                "aria-describedby": ariaDescribedBy.trim() !== '' ? ariaDescribedBy : undefined,
                onDragEnter: handleDragEnter,
                onDragLeave: handleDragLeave,
                onDragOver: handleDragOver,
                onDrop: handleFileChange,
                onClick: ()=>{
                    if (!isUploading) {
                        inputRef?.current?.click();
                    }
                },
                children: /*#__PURE__*/ jsxs("div", {
                    className: styles.removePointerEvents,
                    children: [
                        isUploading ? /*#__PURE__*/ jsx(Spinner, {
                            classNames: {
                                title: styles.spinner
                            },
                            size: 'large',
                            color: 'blue',
                            children: t('fileuploader.InProgressLabel')
                        }) : /*#__PURE__*/ jsx(AttachFileIcon, {
                            className: styles.icon,
                            size: 'large'
                        }),
                        /*#__PURE__*/ jsx("label", {
                            className: styles.innerLabel,
                            htmlFor: id,
                            children: !isUploading && buttonText
                        }),
                        /*#__PURE__*/ jsx("input", {
                            ref: inputRef,
                            "data-testid": `${dataTestId}-input`,
                            type: 'file',
                            accept: acceptedFormatsAsCommaSeparatedString,
                            multiple: multiple,
                            hidden: true,
                            onChange: handleFileChange
                        })
                    ]
                })
            }),
            acceptedFileFormats && /*#__PURE__*/ jsxs("span", {
                id: fileformatsId,
                className: styles.fileInfo,
                children: [
                    acceptedFileFormatsDescription ?? `${t('fileuploader.FormatLabel')} `,
                    /*#__PURE__*/ jsx("span", {
                        className: styles.fileFormatList,
                        children: acceptedFileFormatsDisplay ?? acceptedFormatsAsCommaSeparatedString
                    })
                ]
            }),
            /*#__PURE__*/ jsx(ErrorMessage, {
                id: errorId,
                className: classNames?.errorMessage,
                showError: !!errorMessage,
                children: errorMessage
            }),
            /*#__PURE__*/ jsx(Alert, {
                showAlert: !!uploadResult,
                className: styles.alert,
                variant: uploadResult?.hasUploadFailed ? 'warning' : 'success',
                children: uploadResult?.statusMessage
            }),
            uploadedFiles && /*#__PURE__*/ jsx("ul", {
                className: styles.fileList,
                children: uploadedFiles?.map((file)=>/*#__PURE__*/ jsx(FileUploaderFile, {
                        href: file.href,
                        successIconTitle: successIconTitle,
                        fileIconTitle: fileIconTitle,
                        showSpinner: filesPendingDelete[file.id ?? file.name],
                        onClick: (event)=>{
                            onFileDownload?.(event, file);
                        },
                        onClickDelete: ()=>handleDeleteFile(file),
                        children: file.name
                    }, file.id ?? file.name))
            }),
            /*#__PURE__*/ jsx("div", {
                className: styles.srOnly,
                "aria-live": 'polite',
                "aria-atomic": 'true',
                children: srOnlyText
            })
        ]
    });
});
FileUploader.useFileUploader = useFileUploader;
FileUploader.displayName = 'FileUploader';

export { FileUploader };
